import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { SearchSelectContainer } from './Styles';
import { dummyCallback } from '../ComboBoxSettings';
import { Icon } from 'antd';
import { withTranslation } from 'react-i18next';
import useClickOutsideHandler from '../../containers/ComplexDocumentDesign/HandleClickOutside';
import { emailFunnelChartLabels } from '../../utils/constants/statusMonitoring';
import { ApprovalModal, JobProcessingModal } from '../../utils/complexDocumentConstants';

const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const namespaces = ['SEARCH_SELECT_DROPDOWN'];

const SearchSelectObject = ({
  keyDataSet = ['key 1', 'key2'],
  valueDataSet = {},
  isDynamicKeyValue = false,
  showSelectedKeys,
  placeholder = 'Search key: value',
  selectedFilters = [],
  selectedStatsFilter = [],
  setSelectedFilters = dummyCallback,
  chartCallBack = undefined,
  statsKeys = [],
  allowValuesforKeys = [],
  fullWidth= false,
  t,
  contentType,
  setStatsFilter,
  setSelectedAllRows,
  onFreeTextSearch,
}) => {
  const [searchText, setSearchText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [currentKey, setCurrentKey] = useState(null); // Track selected key
  const [valueOptions, setValueOptions] = useState([]); // Values fetched from backend
  const [openDropDown, setOpenDropDown] = useState(false);
  const [searchSelectFocus, setSearchSelectFocus] = useState(false);
  const optionsListRef = useRef(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const allselectedFilters = [...selectedStatsFilter, ...selectedFilters];
  const filteredKeys = keyDataSet.filter(data => {
    const key = data.toLowerCase();
    return showSelectedKeys
      ? !allselectedFilters.some(filter => filter.key === key) &&
          (!searchText || key.includes(searchText.toLowerCase()))
      : !searchText || key.includes(searchText.toLowerCase());
  });

  const emailChartlabels = useMemo(() => emailFunnelChartLabels(t) ,[]);
  const { sent: sentLabelValue, ...remainingLabels } = emailChartlabels;
  const remainingFunnelValues = Object.values(remainingLabels);  

  const getValuesBasedOnKey = useCallback(
    searchKey => {
      if (searchKey && valueDataSet) {
        const key = searchKey.charAt(0).toUpperCase() + searchKey.slice(1);
        const valuesData = valueDataSet[key] || [];
        const values = valuesData.map(val => val.key);
        return values;
      }
      return [];
    },
    [valueDataSet],
  );

  const matchKeyOrValues = (data, keyOrValue) => {
    return data.some(item => item.toLowerCase() === keyOrValue.toLowerCase());
  };

  const handleSearchChange = e => {
    const text = e.target.value;
    setSearchText(text);

    if (text.includes(': ')) {
      const [key, value] = text.split(': ');
      setCurrentKey(key); // Set the current key
      if (value && matchKeyOrValues(allowValuesforKeys, key)) {
        const filteredValues = getValuesBasedOnKey(key).filter(val =>
          val.toLowerCase().includes(value.toLowerCase()),
        );
        setValueOptions(filteredValues);
      } else {
        if (isDynamicKeyValue) {
          fetchStaticValues(key); // Simulate fetching value options
        } else {
          setValueOptions([]);
        }
      }
    } else {
      setCurrentKey(null); // Reset current key
    }
    setHighlightedIndex(-1); // Reset navigation index
  };

  const fetchStaticValues = debounce(query => {
    if (!query) return;
    setTimeout(() => {
      const key = query.charAt(0).toUpperCase() + query.slice(1);
      const dataSet = valueDataSet[key] || [];
      const values = dataSet.map(val => val.key);
      setValueOptions(values || []);
    }, 200); // Simulate network delay
  }, 300);

  const handleOptionClick = keyOrValue => {
    if (!currentKey) {
      setSearchText(`${keyOrValue}: `);
      setCurrentKey(keyOrValue);
      if (isDynamicKeyValue) {
        fetchStaticValues(keyOrValue); // Simulate fetching value options
      }
    } else {
      if(keyOrValue){
        const filter = { key: currentKey, value: keyOrValue };
        if (statsKeys.includes(currentKey)) {
          const isValuePresent = valueDataSet[currentKey].some(
            val => val.key.toLowerCase() === keyOrValue.toLowerCase(),
          );
          if (isValuePresent) {
            if(remainingFunnelValues.includes(keyOrValue)){
              setStatsFilter(prev => [...prev, filter])
            }else{
              chartCallBack && [ApprovalModal, JobProcessingModal].includes(contentType)
              ? chartCallBack(filter.value, currentKey)
              : setSelectedFilters(prev => [...prev, filter]);
            }
          } else return;
        } else {
          console.log('about to call setSelectedFilters with:', filter);
          setSelectedFilters(prev => [...prev, filter]);
        }

        setSearchText('');
        setCurrentKey(null);
        setValueOptions([]); // Clear value options
      }
    }
    setHighlightedIndex(-1); // Reset navigation index
    searchInputRef.current && searchInputRef.current.focus();
  };

  const handleKeyDown = e => {
    const options = currentKey ? valueOptions : filteredKeys;

    if (e.key === 'Enter') {
      if (highlightedIndex >= 0) {
        // Add the highlighted value to the bubble
        handleOptionClick(options[highlightedIndex]);
      } else if (isDynamicKeyValue && searchText.includes(': ')) {
        const [key, value] = searchText.split(': ').map(str => str.trim());
        handleOptionClick(value);
      } else if (currentKey && !highlightedIndex && !searchText.includes(': ')) {
        // If no option is selected, prevent adding the typed value
        console.log('Please select an option from the list.');
      }
    } else if (e.key === 'ArrowDown') {
      setHighlightedIndex(prev => {
        const newIndex = Math.min(prev + 1, options.length - 1);
        scrollToHighlighted(newIndex);
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex(prev => {
        const newIndex = Math.max(prev - 1, 0);
        scrollToHighlighted(newIndex);
        return newIndex;
      });
    }
  };

  const scrollToHighlighted = index => {
    const optionsContainer = optionsListRef.current;
    const option = optionsContainer?.children[index];

    if (option) {
      const containerRect = optionsContainer.getBoundingClientRect();
      const optionRect = option.getBoundingClientRect();

      if (optionRect.bottom > containerRect.bottom) {
        // Scroll down if the option is below the visible area
        optionsContainer.scrollTop += optionRect.bottom - containerRect.bottom;
      } else if (optionRect.top < containerRect.top) {
        // Scroll up if the option is above the visible area
        optionsContainer.scrollTop -= containerRect.top - optionRect.top;
      }
    }
  };

  const handleRemoveFilter = filter => {
    const isSelectedFilters = selectedFilters.some(
      item => item.key === filter.key && item.value === filter.value,
    );
    const isStatsFilter = selectedStatsFilter.some(
      item => item.key === filter.key && item.value === filter.value,
    );
    if (isSelectedFilters) {
      setSelectedFilters(prev =>
        prev.filter(item => !(item.key === filter.key && item.value === filter.value)),
      );
    }
    if (isStatsFilter) {
      if(remainingFunnelValues.includes(filter.value)){
        setStatsFilter(prev =>
          prev.filter(item => !(item.key === filter.key && item.value === filter.value)),
        );
      }else{
      chartCallBack
        ? chartCallBack(filter.value, filter.key)
        : setSelectedFilters(prev =>
            prev.filter(item => !(item.key === filter.key && item.value === filter.value)),
          );
        }
    } else {
      setSelectedFilters(prev =>
        prev.filter(item => !(item.key === filter.key && item.value === filter.value)),
      );  
    }
  };

  const handleClickOutside = event => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setOpenDropDown(false);
    }
  };

  const focusSearchInput = () => {
    setSearchSelectFocus(true);
    searchInputRef.current && searchInputRef.current.focus();
  };

  const deFocusSearchInput = () => {
    setSearchSelectFocus(false);
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchText]);

  useClickOutsideHandler({ref: containerRef, clickInsideHandler: focusSearchInput, clickOutsideHandler: deFocusSearchInput} );

  return (
    <SearchSelectContainer
      ref={containerRef}
      searchSelectFocus={searchSelectFocus || fullWidth}
      isFocused={searchSelectFocus}
      tags={allselectedFilters}
      highlightedIndex={highlightedIndex}
    >
      <div className={`search-input-wrapper${searchSelectFocus ? ' highlight' : ''}`}>
        {allselectedFilters.length > 0 &&
          allselectedFilters.map((filter, index) => (
            <span className="filter-bubble" key={index}>
              {filter.key}: {filter.value}
              <Icon type={'close-circle'} onClick={() => handleRemoveFilter(filter)} />
            </span>
          ))
        }
        <input
          type="text"
          className="search-input"
          value={searchText}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          onFocus={() => setOpenDropDown(true)}
          ref={searchInputRef}
        />
      </div>
      {openDropDown && (
        <ul className={`options-list bottom`} ref={optionsListRef} onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}>
          {currentKey && valueOptions.length > 0 && isDynamicKeyValue
            ? valueOptions.map((value, index) => (
                <li
                  key={index}
                  className={`option ${index === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleOptionClick(value)}
                  onMouseOver={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                >
                  {value}
                </li>
              ))
            : !currentKey && filteredKeys.length > 0
            ? filteredKeys.map((value, index) => (
                <li
                  key={index}
                  className={`option ${index === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleOptionClick(value)}
                  onMouseOver={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                >
                  {value}
                </li>
              ))
            : null}
        </ul>
      )}
    </SearchSelectContainer>
  );
};

export default withTranslation(namespaces)(SearchSelectObject);
